import {ITemplateListeItemModel, ITemplateModel} from "../../Model/Template/ITemplateModel.js";
import {TemplateEditor} from "./TemplateEditor.js";
import {ComponentPanel} from "../ComponentPanel.js";
import {OnTemplateAdd, OnTemplateDelete, OnTemplateGroupSaved, OnTemplateUpdate} from "../../Delegates/TemplateDelegate.js";
import {KendoDataObject} from "../../Tools/Misc.js";
import {TemplateRead} from "../../Controller/Template/TemplateRead.js";
import {RessourceSlot} from "../../Tools/Disposable/RessourceSlot";
import {IDisposableLike} from "../../Tools/Disposable/IDisposableLike";
import {TemplateCreateController} from "../../Controller/Template/TemplateCreate";
import {PackageImportDialog} from "../Package/PackageImportDialog.js";
import {PackageImporter} from "../../Tools/Package/PackageImporter.js";
import {NotificationHelper} from "../../Helper/NotificationHelper.js";

export class TemplateListItem implements IDisposableLike {
    #jqContainer: JQuery | undefined;
    #jqGrid: JQuery | undefined;
    #jqGridContainer: JQuery | undefined;
    #jqMainContainer: JQuery | undefined;
    #kGrid: kendo.ui.Grid | undefined;
    #projetId: string | undefined;
    #selectedItem: kendo.data.ObservableObject;
    #tempArray: Map<string, ITemplateModel> = new Map<string, ITemplateModel>();
    readonly #templateSlot = new RessourceSlot<ComponentPanel>()

    constructor(option: ITemplateListItemOption) {
        this.#jqMainContainer = option.jqMainContainer;
        this.#projetId = option.projetId;
    }

    public Init = async () => {
        this.#buildHtml();
        this.#buildGrid().then();
        this.#initEvent().then();
    }

    #buildGrid = async () => {
        this.#kGrid = this.#jqGrid.kendoGrid({
            toolbar: [
                {
                    type: "button", text: "Ajouter", icon: "plus", click: async (e: any) => this.#addTemplate()
                },
                {
                    type: "button", text: "Importer", icon: "upload", click: async () => this.#importTemplate()
                }
            ],
            dataSource: await TemplateRead.ReadList({AsNoTracking: true, FkProjet: this.#projetId}),
            size: "small",
            width: "100%",
            height: "100%",
            selectable: "row",
            columns: [
                {selectable: true, width: "50px"},
                {title: "Nom", field: "Nom"},
                {title: "Libelle", field: "Libelle"},
                {title: "Type", field: "TypeContentLibelle"},
                {title: "Processus", field: "TypeProcessLibelle"},
            ],
            change: async (e: kendo.ui.GridChangeEvent) => {
                if (!e.sender.select().length)
                    return;

                this.#selectedItem = e.sender.dataItem(e.sender.select());
                const m = await this.#readModel();

                // Pas besoin d'implémenter le disposable Pattern.
                // La ressource est disposée lors de la fermeture de ComponentPanel.
                const t = new TemplateEditor({
                    model: m
                });

                const c = this.#templateSlot.Reset(() => {
                    return new ComponentPanel({
                        jqMainContainer: $("[circ-id='projet-tab-container']"),
                        titre: m.Nom,
                    });
                });
                await c.Init();
                await c.Open(t);
                await c.Maximize();
            },
        }).data("kendoGrid");
    }

    #buildHtml = () => {
        this.#jqContainer = $("<div></div>")
            .css({
                display: "flex",
                flexDirection: "column",
                width: "100%",
                height: "100%",
            })
            .appendTo(this.#jqMainContainer);

        this.#jqGridContainer = $("<div></div>")
            .css({flexGrow: 1, width: "100%"})
            .appendTo(this.#jqContainer);

        this.#jqGrid = $("<div></div>")
            .appendTo(this.#jqGridContainer);

    }

    #initEvent = async () => {
        await OnTemplateAdd.Clear("TemplateListItem");
        await OnTemplateUpdate.Clear("TemplateListItem");
        await OnTemplateDelete.Clear("TemplateListItem");
        await OnTemplateGroupSaved.Clear("TemplateListItem");

        // Après enregistrement d'un groupe i18n, on purge l'entrée de cache du
        // pivot : la prochaine ouverture rechargera toutes les langues via
        // l'API (sinon on garderait une version mono-langue en mémoire).
        OnTemplateGroupSaved.Bind((pivotPrimarykey: string) => {
            this.#tempArray.delete(pivotPrimarykey);
        }, "TemplateListItem");

        // Ajout : insère la ligne créée puis la sélectionne (ouvre l'éditeur
        // via le handler `change`). Déclenché par TemplateCreateController.
        OnTemplateAdd.Bind((model: ITemplateModel) => {
            const m = this.#kGrid.dataSource.add(model);
            this.#tempArray.set(model.Primarykey, model);
            const node = this.#kGrid.tbody.find(`tr[data-uid='${m.uid}']`);
            this.#kGrid.select(node);
            this.#kGrid.trigger("change", {sender: this.#kGrid} as any);
        }, "TemplateListItem");

        OnTemplateUpdate.Bind((model: ITemplateModel) => {
            // On ne re-cache PAS le modèle ici : la réponse d'upsert est
            // mono-langue (sans Translations) et écraserait le groupe enrichi.
            // Le cache du groupe est purgé par OnTemplateGroupSaved, puis
            // rechargé par #readModel. On se limite à rafraîchir la ligne.
            KendoDataObject.CopyFull(new kendo.data.ObservableObject(model), this.#selectedItem);
            const node = this.#kGrid.tbody.find(`tr[data-uid='${this.#selectedItem.uid}']`);
            this.#kGrid.select(node);
        }, "TemplateListItem");

        OnTemplateDelete.Bind((model: ITemplateModel) => {
            if (this.#selectedItem.get("Primarykey") == model.Primarykey)
                this.#kGrid.dataSource.remove(this.#selectedItem);
            this.#tempArray.delete(model.Primarykey);
            this.#templateSlot.Dispose();

        }, "TemplateListItem");
    }

    #readModel = async () => {
        const m = this.#selectedItem.toJSON() as any as ITemplateListeItemModel;
        if (this.#tempArray.has(m.Primarykey))
            return this.#tempArray.get(m.Primarykey);

        // On charge tout le groupe i18n (pivot + traductions) en une requête,
        // puis on rattache les traductions au pivot via le champ transitoire
        // Translations. C'est le pivot qu'on ouvre dans l'éditeur.
        return await TemplateRead.Read({
            Primarykey: m.Primarykey,
            AsNoTracking: true,
            IncludeDatas: true,
            IncludeTranslations: true
        })
            .then((data) => {
                const group = data as Array<ITemplateModel>;
                const pivot = group.find(x => !x.FkParent) ?? group[0];
                pivot.Translations = group.filter(x => x.Primarykey !== pivot.Primarykey);
                this.#tempArray.set(pivot.Primarykey, pivot);
                return pivot;
            })
    }

    #addTemplate = async () => {
        // L'ajout de ligne et la sélection sont pris en charge par
        // l'abonnement à OnTemplateAdd (cf. #initEvent), déclenché par
        // TemplateCreateController. On se contente de demander la création.
        await TemplateCreateController.Create(this.#projetId);
    }

    // Import d'un paquet « easymail-template/1 » (produit par un agent IA ou
    // exporté d'un autre template) : crée le template, ses traductions et
    // l'éventuelle stratégie OTP, puis ouvre l'éditeur via OnTemplateAdd.
    #importTemplate = async () => {
        const pkg = await PackageImportDialog.Open({
            title: "Importer un template",
            intro: "Crée un nouveau template dans ce projet à partir d'un paquet produit par un agent IA " +
                "(skill easymail-templates) ou exporté d'un autre template. Si le paquet contient une " +
                "stratégie OTP, elle est créée aussi.",
            context: {mode: "create-template", fkProjet: this.#projetId},
            describe: PackageImporter.DescribeCreate,
            confirmText: "Créer",
        });
        if (!pkg)
            return;

        const result = await PackageImporter.Create(pkg, this.#projetId);
        if (!result)
            return;

        OnTemplateAdd.Fire(result.pivot);
        NotificationHelper.Instance.success(result.otpStrategy
            ? `Template et stratégie OTP « ${result.otpStrategy.Nom} » créés (voir l'onglet OTP).`
            : "Template créé : vérifiez la prévisualisation.");
    }

    public Dispose = async () => {
        await OnTemplateUpdate.Clear("TemplateListItem");
        await OnTemplateAdd.Clear("TemplateListItem");
        await OnTemplateDelete.Clear("TemplateListItem");
        await OnTemplateGroupSaved.Clear("TemplateListItem");
        this.#kGrid.destroy();
        this.#jqContainer.remove();
        this.#templateSlot.Dispose();
    }
}

export interface ITemplateListItemOption {
    jqMainContainer: JQuery;
    projetId: string;
}
